/*
  VF custom logo printing.

  The important part of this file is the submit interception.

  Shopify's AJAX Cart API does not accept file uploads. Line item property
  files only arrive if the product form is submitted as a normal browser POST
  with enctype="multipart/form-data". Anything that adds to cart over the Cart
  API instead will serialise the file input as its .value, which is the
  browser's "C:\fakepath\name.png" placeholder, and the artwork is lost.

  Two different things try to do that:

    1. Dawn's <product-form> element, which binds a submit handler on the form.
    2. App embeds (bundle, volume discount and pre-order apps are the usual
       ones) which bind their own handler to the add to cart BUTTON and call
       preventDefault on the click. When one of those wins, no submit event is
       ever raised, so a submit listener alone never runs.

  So we intercept in two places, both on document in the CAPTURE phase, which
  runs before any listener bound to the button or the form no matter who
  registered first:

    - click, for the ordinary case.
    - submit, for keyboard submits, which never produce a click on the button.

  In both cases we take the event, rewrite the form field names into Shopify's
  indexed items[] format so the garment and the printing fee are added as two
  line items in a single POST tied together by a shared reference, and then
  call form.submit() ourselves rather than relying on the browser's default
  action surviving.
*/

if (!customElements.get('vf-printing')) {
  customElements.define(
    'vf-printing',
    class VfPrinting extends HTMLElement {
      constructor() {
        super();

        this.MAX_BYTES = 20 * 1024 * 1024;
        this.VECTOR = ['ai', 'eps', 'pdf', 'svg'];
        this.state = { on: false, option: 'front', back: 'same' };
        this.submitting = false;

        this.form = document.getElementById(this.dataset.formId);
        this.head = this.querySelector('[data-vf-head]');
        this.body = this.querySelector('[data-vf-body]');
        this.errorEl = this.querySelector('[data-vf-error]');

        this.frontVariant = this.dataset.frontVariant;
        this.bothVariant = this.dataset.bothVariant;

        if (!this.form) return;

        this.bindToggle();
        this.bindOptions();
        this.bindPlaces();
        this.bindUploads();
        this.bindName();
        this.bindSubmit();

        this.render();
      }

      /* ---------- toggle ---------- */

      bindToggle() {
        const flip = () => this.setOn(!this.state.on);
        this.head.addEventListener('click', flip);
        this.head.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            flip();
          }
        });
      }

      setOn(on) {
        this.state.on = on;
        this.classList.toggle('vf-print--on', on);
        this.head.setAttribute('aria-expanded', String(on));
        this.body.hidden = !on;
        this.render();
      }

      /* ---------- print option ---------- */

      bindOptions() {
        this.querySelector('[data-vf-opts]').addEventListener('click', (e) => {
          const btn = e.target.closest('[data-vf-opt]');
          if (!btn) return;
          this.selectRadio(btn, '[data-vf-opt]');
          this.state.option = btn.dataset.vfOpt;
          this.render();
        });

        this.querySelector('[data-vf-backopts]').addEventListener('click', (e) => {
          const btn = e.target.closest('[data-vf-back]');
          if (!btn) return;
          this.selectRadio(btn, '[data-vf-back]');
          this.state.back = btn.dataset.vfBack;
          this.render();
        });
      }

      selectRadio(btn, selector) {
        btn.parentElement.querySelectorAll(selector).forEach((b) => b.setAttribute('aria-checked', 'false'));
        btn.setAttribute('aria-checked', 'true');
      }

      /* ---------- front position ---------- */

      bindPlaces() {
        this.querySelector('[data-vf-places]').addEventListener('click', (e) => {
          const btn = e.target.closest('[data-vf-place]');
          if (!btn) return;
          this.selectRadio(btn, '[data-vf-place]');
          this.querySelector('[data-vf-placeprop]').value = btn.dataset.vfPlace;
          this.querySelector('[data-vf-placehint]').textContent = btn.dataset.vfHint;
        });
      }

      /* ---------- uploads ---------- */

      bindUploads() {
        this.querySelectorAll('[data-vf-file]').forEach((input) => {
          const which = input.dataset.vfFile;
          const drop = input.previousElementSibling;

          input.addEventListener('change', () => this.handleFile(input, which));

          ['dragenter', 'dragover'].forEach((ev) =>
            drop.addEventListener(ev, (e) => {
              e.preventDefault();
              drop.classList.add('is-over');
            })
          );
          ['dragleave', 'drop'].forEach((ev) =>
            drop.addEventListener(ev, (e) => {
              e.preventDefault();
              drop.classList.remove('is-over');
            })
          );
          drop.addEventListener('drop', (e) => {
            const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
            if (!file) return;
            const dt = new DataTransfer();
            dt.items.add(file);
            input.files = dt.files;
            this.handleFile(input, which);
          });
        });

        this.querySelectorAll('[data-vf-remove]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const which = btn.dataset.vfRemove;
            const input = this.querySelector('[data-vf-file="' + which + '"]');
            input.value = '';
            this.querySelector('[data-vf-filecard="' + which + '"]').hidden = true;
            const flag = this.querySelector('[data-vf-flag="' + which + '"]');
            if (flag) flag.hidden = true;
            this.render();
          });
        });
      }

      handleFile(input, which) {
        const file = input.files && input.files[0];
        if (!file) return;

        const ext = (file.name.split('.').pop() || '').toLowerCase();

        if (file.size > this.MAX_BYTES) {
          input.value = '';
          this.showError('That file is over the 20MB limit. Please send a smaller version.');
          this.render();
          return;
        }
        this.showError(null);

        const card = this.querySelector('[data-vf-filecard="' + which + '"]');
        card.hidden = false;
        this.querySelector('[data-vf-filename="' + which + '"]').textContent = file.name;
        this.querySelector('[data-vf-filesize="' + which + '"]').textContent =
          this.readableSize(file.size) + ' \u00B7 ' + ext.toUpperCase();

        const thumb = this.querySelector('[data-vf-thumb="' + which + '"]');
        thumb.textContent = ext.toUpperCase();
        if (/^image\//.test(file.type)) {
          const reader = new FileReader();
          reader.onload = (ev) => {
            thumb.textContent = '';
            const img = document.createElement('img');
            img.src = ev.target.result;
            img.alt = '';
            thumb.appendChild(img);
          };
          reader.readAsDataURL(file);
        }

        const flag = this.querySelector('[data-vf-flag="' + which + '"]');
        if (flag) {
          const advice = this.adviseOn(ext);
          flag.hidden = false;
          flag.textContent = advice.text;
          flag.classList.toggle('vf-print__flag--warn', !advice.ok);
        }

        this.render();
      }

      adviseOn(ext) {
        if (this.VECTOR.indexOf(ext) > -1) {
          return { ok: true, text: 'Vector artwork. Prints crisp at any size, including the large back print.' };
        }
        if (ext === 'png') {
          return {
            ok: true,
            text: 'PNG accepted. Make sure it is high resolution with a transparent background, or your logo may print inside a white box.',
          };
        }
        return {
          ok: false,
          text: 'JPEGs cannot hold a transparent background, so this may print on a white rectangle. Send a PNG or vector file if you have one. We will check it either way.',
        };
      }

      readableSize(bytes) {
        return bytes > 1048576
          ? (bytes / 1048576).toFixed(1) + ' MB'
          : Math.max(1, Math.round(bytes / 1024)) + ' KB';
      }

      /* ---------- personalisation ---------- */

      bindName() {
        const input = this.querySelector('.vf-print__text');
        const count = this.querySelector('[data-vf-count]');
        input.addEventListener('input', () => {
          count.textContent = input.value.length + ' / 22';
        });
      }

      /* ---------- state ---------- */

      get needsBackFile() {
        return this.state.option === 'both' && this.state.back === 'different';
      }

      hasFile(which) {
        const input = this.querySelector('[data-vf-file="' + which + '"]');
        return !!(input && input.files && input.files.length);
      }

      get blocked() {
        if (!this.state.on) return false;
        if (!this.hasFile('front')) return 'Upload your logo to continue';
        if (this.needsBackFile && !this.hasFile('back')) return 'Upload your back artwork to continue';
        return false;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.hidden = !message;
        this.errorEl.textContent = message || '';
      }

      render() {
        const both = this.state.option === 'both';

        this.querySelector('[data-vf-backblock]').hidden = !both;
        this.querySelector('[data-vf-backupload]').hidden = !this.needsBackFile;
        if (!both) this.state.back = 'same';

        // Fields only submit when printing is switched on, and the back file
        // only when it is genuinely a separate upload.
        this.querySelectorAll('[data-vf-file], .vf-print__text, [data-vf-optprop], [data-vf-placeprop], [data-vf-ref]').forEach(
          (el) => {
            const isBackFile = el.dataset && el.dataset.vfFile === 'back';
            el.disabled = !this.state.on || (isBackFile && !this.needsBackFile);
          }
        );

        const label = both ? 'Front and back' : 'Front logo only';
        this.querySelector('[data-vf-optprop]').value = label;
        this.querySelector('[data-vf-sumlabel]').textContent = both
          ? 'Printing, front and back'
          : 'Printing, front logo';

        const cost = this.querySelector('[data-vf-opt="' + (both ? 'both' : 'front') + '"] .vf-print__optcost');
        this.querySelector('[data-vf-sumcost]').textContent = cost ? cost.textContent : '';

        this.updateSubmitButton();
      }

      updateSubmitButton() {
        const button = this.form.querySelector('[type="submit"]');
        if (!button) return;
        const span = button.querySelector('span');
        const blocked = this.blocked;

        if (blocked) {
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('vf-print__blocked');
          if (span) {
            if (!this._buttonLabel) this._buttonLabel = span.textContent;
            span.textContent = blocked;
          }
        } else {
          button.classList.remove('vf-print__blocked');
          button.removeAttribute('aria-disabled');
          if (span && this._buttonLabel) span.textContent = this._buttonLabel;
        }
      }

      /* ---------- submit ---------- */

      bindSubmit() {
        // Capture phase on document, so we run before any handler bound to
        // the button or the form, whichever script registered first.
        document.addEventListener('click', (evt) => this.onAddToCartClick(evt), true);

        // Keyboard submits never produce a click on the button, so the submit
        // event stays as a second entry point.
        document.addEventListener('submit', (evt) => this.onFormSubmit(evt), true);

        // Restore the button if the customer comes back via the back button,
        // where the page is served from the cache with our flag still set.
        window.addEventListener('pageshow', () => {
          this.submitting = false;
        });
      }

      // Returns the add to cart button for this product form when the event
      // came from it, otherwise null. Dynamic checkout buttons (Shop Pay and
      // friends) are not name="add" and are deliberately left alone.
      addToCartButtonFor(evt) {
        const target = evt.target;
        if (!target || typeof target.closest !== 'function') return null;

        const button = target.closest('button[type="submit"], input[type="submit"]');
        if (!button || button.name !== 'add') return null;
        if (button.form !== this.form && !this.form.contains(button)) return null;

        return button;
      }

      onAddToCartClick(evt) {
        if (!this.state.on) return;
        if (!this.addToCartButtonFor(evt)) return;

        // Nothing downstream gets to see this click.
        evt.preventDefault();
        evt.stopImmediatePropagation();

        if (this.blocked) {
          this.reportBlocked();
          return;
        }

        this.postNatively();
      }

      onFormSubmit(evt) {
        if (evt.target !== this.form) return;
        if (!this.state.on) return;

        evt.preventDefault();
        evt.stopImmediatePropagation();

        if (this.blocked) {
          this.reportBlocked();
          return;
        }

        this.postNatively();
      }

      // Writes the indexed fields and posts the form as a real multipart POST.
      postNatively(rows) {
        if (this.submitting) return;
        this.submitting = true;

        if (rows) {
          this.writeIndexedPost(rows);
        } else {
          this.prepareNativePost();
        }

        // form.submit() does not fire the submit event, so this does not
        // re-enter the interceptors above.
        this.form.submit();
      }

      reportBlocked() {
        this.showError(this.blocked);
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      /* ---------- public API, used by the bulk size grid ---------- */

      // True when printing is switched on for this product form.
      get isActive() {
        return !!this.state.on;
      }

      // The bulk size grid hands its rows here rather than posting them over
      // AJAX, because the Cart API discards file uploads. Returns true once it
      // has taken the submit over. Rows are [{ id, quantity }].
      submitRows(rows) {
        if (!this.state.on) return false;
        if (!rows || !rows.length) return false;

        if (this.blocked) {
          this.reportBlocked();
          return true;
        }

        this.postNatively(rows);
        return true;
      }

      /* ---------- native post ---------- */

      prepareNativePost() {
        const quantityInput = this.form.querySelector('[name="quantity"]');
        const idInput = this.form.querySelector('[name="id"]');

        this.writeIndexedPost([
          {
            id: idInput ? idInput.value : null,
            quantity: quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1,
          },
        ]);
      }

      // Rewrites the form into Shopify's indexed items[] format for any number
      // of garment lines, followed by a single printing fee line.
      writeIndexedPost(rows) {
        this.form.setAttribute('enctype', 'multipart/form-data');

        // A second pass must not leave two sets of indexed fields behind,
        // which is what produced the mismatched print references on orders
        // 21430 and 21512.
        this.form.querySelectorAll('[data-vf-injected]').forEach((el) => el.remove());

        const reference = 'VFP-' + Date.now().toString(36).toUpperCase();
        this.querySelector('[data-vf-ref]').value = reference;

        // The single variant id and quantity are superseded by the indexed
        // fields below, so they must not also be submitted.
        const idInput = this.form.querySelector('[name="id"]');
        const quantityInput = this.form.querySelector('[name="quantity"]');
        if (idInput) idInput.disabled = true;
        if (quantityInput) quantityInput.disabled = true;

        rows.forEach((row, i) => {
          this.addHidden('items[' + i + '][id]', row.id);
          this.addHidden('items[' + i + '][quantity]', row.quantity);
        });

        // Text properties are repeated on every printed garment line so the
        // picker sees the print details against each size. A file can only be
        // submitted from its own input, so the artwork rides on the first line
        // and _vf_print_ref ties the remaining lines to it.
        const textProps = [];
        this.querySelectorAll('[name^="properties["]').forEach((el) => {
          if (el.disabled) return;
          const key = el.name.replace(/^properties\[/, '').replace(/\]$/, '');
          if (el.type === 'file') {
            el.name = 'items[0][properties][' + key + ']';
          } else {
            textProps.push({ key: key, value: el.value });
            el.disabled = true;
          }
        });

        rows.forEach((row, i) => {
          textProps.forEach((prop) => {
            if (prop.value === '') return;
            this.addHidden('items[' + i + '][properties][' + prop.key + ']', prop.value);
          });
        });

        // Printing is charged per item, so the fee quantity is the total units
        // across every garment line.
        const units = rows.reduce((n, row) => n + row.quantity, 0);
        const feeVariant = this.state.option === 'both' ? this.bothVariant : this.frontVariant;
        const fee = rows.length;

        this.addHidden('items[' + fee + '][id]', feeVariant);
        this.addHidden('items[' + fee + '][quantity]', units);
        this.addHidden('items[' + fee + '][properties][_vf_print_ref]', reference);
        this.addHidden('items[' + fee + '][properties][For]', this.form.dataset.vfProductTitle || document.title);
      }

      addHidden(name, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.setAttribute('data-vf-injected', '');
        this.form.appendChild(input);
      }
    }
  );
}
